'use client';

import React from 'react';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import type {} from '@mui/x-date-pickers/themeAugmentation';
import type {} from '@mui/x-date-pickers-pro/themeAugmentation';
import { inputBaseClasses } from '@mui/material/InputBase';
import {
	buttonClasses,
	buttonGroupClasses,
	inputAdornmentClasses,
	outlinedInputClasses,
} from '@mui/material';
import { BlockingLink } from '../../blocks/BlockingLink';
import { appTokens } from '../../theme/tokens';

const defaultElevation = 3;
type Tuple = [string, string];
type TupleArray<Tuple, TupleLen extends string> = [Tuple, ...Tuple[]] & {
	length: TupleLen;
};
/* elevation 2 was `0px 3px 1px -2px rgba(0,0,0,0.2),0px 2px 2px 0px rgba(0,0,0,0.14),0px 1px 5px 0px rgba(0,0,0,0.12)`
but we wanted to darker but not spread out more
*/
const shadows: TupleArray<Tuple, '25'> = Object.keys( [ ...Array( 25 ) ] ).map(
	( value ) =>
		`0px ${ value }px 1px -2px rgba(0,0,0,0.25),0px 2px 2px 0px rgba(0,0,0,0.18),0px 1px 5px 0px rgba(0,0,0,0.16)`
) as TupleArray<Tuple, '25'>;

const theme = createTheme( {
	shadows,
	shape: {
		// assume REM value
		borderRadius: parseFloat( appTokens.shape.rounded.replace( 'rem', '' ) ) * 16,
	},
	palette: {
		primary: {
			main: appTokens.palette.primary,
		},
		secondary: {
			main: appTokens.palette.secondary,
		},
		error: {
			main: appTokens.palette.error,
			dark: appTokens.derived.errorDark,
			contrastText: appTokens.palette.white,
		},
	},
	typography: {
		fontFamily: '\'Inter\', sans-serif',
		h1: {
			fontWeight: '400',
			fontSize: '2.25rem',
			fontFamily: '\'Instrument Serif\', serif',
		},
		h2: {
			fontWeight: 'bold',
			fontSize: '1.2rem',
			lineHeight: '2.25rem',
		},
		h5: {
			fontWeight: 'bold',
			fontSize: '1rem',
			lineHeight: '2rem',
		},
		h6: {
			fontWeight: 'bold',
			fontSize: '0.875rem',
			lineHeight: '2rem',
		},
		body1: {
			fontSize: '0.875rem',
			lineHeight: '1.375rem',
		},
		caption: {
			fontSize: '0.875rem',
			color: appTokens.palette.readableGray,
		},
		button: {
			textTransform: 'unset',
			fontSize: '0.875rem',
		},
	},
	components: {
		MuiDialog: {
			defaultProps: {
				maxWidth: false,
			},
			styleOverrides: {
				paper: ( { theme } ) => ( {
					margin: 0,
					maxWidth: '100%',
					[ theme.breakpoints.up( 'md' ) ]: {
						width: 'max-content',
					},
				} ),
				paperWidthFalse: {
					maxWidth: '100%',
				},
			},
		},
		MuiDialogTitle: {
			defaultProps: {
				variant: 'h1',
			},
		},
		MuiFormLabel: {
			styleOverrides: {
				root: ( { ownerState } ) => ( {
					color:
						ownerState.color === 'primary'
							? appTokens.palette.primary
							: appTokens.palette.readableGray,
					fontSize: '0.875rem',
				} ),
			},
		},
		MuiTypography: {
			defaultProps: {
				variant: 'body1',
				color: 'primary',
			},
		},
		MuiAvatar: {
			styleOverrides: {
				root: {
					backgroundColor: appTokens.palette.white,
					color: appTokens.palette.primary,
					borderColor: appTokens.palette.lightBorderColor,
					borderWidth: 1,
					borderStyle: 'solid',
				},
			},
		},
		MuiAvatarGroup: {
			styleOverrides: {
				root: {
					borderColor: 'transparent',
					[ '.MuiAvatar-root .MuiAvatar-root' ]: {
						backgroundColor: appTokens.palette.white,
						color: appTokens.palette.primary,
						borderColor: appTokens.palette.lightBorderColor,
						borderWidth: 1,
						borderStyle: 'solid',
						borderRadius: '100%',
					},
				},
			},
		},
		MuiInputBase: {
			styleOverrides: {
				// iOS Safari auto-zooms the viewport when focusing an input with a
				// computed font-size under 16px, and that zoom sticks around after a
				// client-side route change (there's no full page load to reset it).
				// Bumping to 1rem on small screens avoids triggering the zoom at all.
				root: ( { theme } ) => ( {
					color: appTokens.palette.primary,
					[ theme.breakpoints.down( 'sm' ) ]: {
						fontSize: '1rem',
					},
					// Change the background of disabled inputs, but only if they're outlined
					[ `&.${ outlinedInputClasses.root }.${ inputBaseClasses.disabled }` ]: {
						background: '#F5F5F5',
						[ `.${ inputAdornmentClasses.root }` ]: {
							color: appTokens.palette.readableGray,
						},
					},
				} ),
			},
		},
		MuiInput: {
			styleOverrides: {
				root: {
					'&:hover:not(.Mui-disabled, .Mui-error)::before': {
						borderBottomColor: appTokens.derived.primaryLight,
					},
				},
			},
		},
		MuiPickersInput: {
			styleOverrides: {
				root: {
					'&:hover:not(.Mui-disabled, .Mui-error)::before': {
						borderBottomColor: appTokens.derived.primaryLight,
					},
				},
			},
		},
		MuiInputAdornment: {
			styleOverrides: {
				root: {
					color: appTokens.palette.primary,
				},
			},
		},
		MuiOutlinedInput: {
			styleOverrides: {
				root: {
					[ `& .${ outlinedInputClasses.notchedOutline }` ]: {
						borderColor: appTokens.palette.lightBorderColor,
					},
					[ `&:not(.${ inputBaseClasses.disabled }):hover:not(:focus-within)` ]: {
						[ `& .${ outlinedInputClasses.notchedOutline }` ]: {
							borderColor: appTokens.palette.primary,
						},
					},
					'& input::placeholder': {
						color: appTokens.palette.readableGray,
						opacity: 1,
					},
				},
			},
		},
		MuiChip: {
			defaultProps: {
				color: 'secondary',
			},
			styleOverrides: {
				root: {
					color: appTokens.palette.primary,
					fontSize: '0.8125rem',
				},
			},
		},
		MuiButton: {
			defaultProps: {
				variant: 'contained',
				color: 'secondary',
				disableElevation: true, // no dropshadow
			},
			styleOverrides: {
				root: ( { ownerState } ) => ( {
					lineHeight: '1.375rem',
					fontSize: '1rem',
					whiteSpace: 'nowrap',
					[ `&.${ buttonClasses.contained }.${ buttonClasses.colorPrimary }` ]: {
						backgroundColor: appTokens.palette.primary,
						color: appTokens.palette.secondary,
						'&:hover': {
							backgroundColor: appTokens.derived.primaryLight,
							color: appTokens.palette.secondary,
						},
					},
					[ `&.${ buttonClasses.contained }.${ buttonClasses.colorSecondary }` ]: {
						backgroundColor: appTokens.palette.secondary,
						color: appTokens.palette.primary,
						'&:hover': {
							backgroundColor: appTokens.derived.primaryLight,
							color: appTokens.palette.secondary,
						},
					},
					[ `&.${ buttonClasses.contained }.${ buttonClasses.colorError }` ]: {
						color: appTokens.palette.white,
						'&:hover': {
							backgroundColor: appTokens.derived.errorDark,
							color: appTokens.palette.white,
						},
					},
					[ `&.${ buttonClasses.outlined }:not(.${ buttonClasses.colorError })` ]: {
						backgroundColor: appTokens.derived.secondaryLightest,
						borderColor: appTokens.palette.secondary,
						color: appTokens.palette.secondary,
						'&:not(.Mui-disabled):hover': {
							backgroundColor: appTokens.derived.secondaryLightest,
							borderColor: appTokens.derived.primaryLight,
							color: appTokens.derived.primaryLight,
						},
					},
					'.MuiButton-startIcon, .MuiButton-endIcon': {
						color: 'inherit',
						width: '1.125em',
						minWidth: '1.125em',
						height: '1.125em',
						fill: 'currentColor',
						'& svg': {
							fill: 'currentColor',
							minWidth: '100%',
						},
					},
					...( [ 'outlined', 'contained' ].includes( ownerState.variant || '' )
						? ownerState.startIcon // icon button
							? {
								padding: '1rem 2.5rem',
								minWidth: '10.25rem',
								minHeight: '3.5rem',
								'&.empty': {
									minWidth: '3.5rem',
									maxWidth: '3.5rem',
									padding: '1rem 0',
									'.MuiButton-startIcon': {
										margin: 0,
									},
								},
							  }
							: {
								// non-icon, non-text button
								padding: '0.625rem 2.5rem',
								minWidth: '7.5rem',
							  }
						: {
							// everything else
							padding: '0.625rem 1rem',
							textTransform: 'uppercase',
						  } ),
					...( 'text' === ownerState.variant && 'secondary' === ownerState.color
						? {
							color: appTokens.palette.primary,
							backgroundColor: appTokens.derived.secondaryLightest,
							'&:hover': {
								color: appTokens.derived.primaryLight,
								backgroundColor: appTokens.palette.secondary,
							},
						  }
						: undefined ),
					'&&.Mui-disabled': {
						backgroundColor: appTokens.derived.disabledBackground,
						borderColor: appTokens.derived.disabledBackground,
						color: appTokens.derived.disabledText,
						cursor: 'not-allowed',
						fill: 'currentColor',
						'&:not([href])': {
							pointerEvents: 'auto',
						},
						'&:hover': {
							backgroundColor: appTokens.derived.disabledBackgroundHover,
							borderColor: appTokens.derived.disabledBackgroundHover,
							color: appTokens.derived.disabledText,
						},
					},
					[ `&&.${ buttonClasses.loading }` ]: {
						color: 'transparent',
						'&:hover': {
							color: 'transparent',
						},
						[ `.${ buttonClasses.loadingIndicator }` ]: {
							color: appTokens.derived.disabledText,
						},
					},
				} ),
			},
		},
		MuiButtonGroup: {
			styleOverrides: {
				root: {
					'&.empty': {
						[ `.${ buttonGroupClasses.grouped }` ]: {
							minWidth: '3.5rem',
							padding: '1rem 0',
						},
						'.MuiButton-startIcon': {
							margin: 0,
						},
					},
				},
			},
		},
		MuiFormHelperText: {
			styleOverrides: {
				root: {
					fontSize: '0.6875rem',
					color: appTokens.palette.readableGray,
				},
			},
		},
		// Some unfortunate style overrides below for DatePicker, because Mui doesn't want us to style those easily I guess...
		MuiPaper: {
			defaultProps: {
				elevation: defaultElevation,
			},
			styleOverrides: {
				root: {
					'.MuiPickersCalendarHeader-labelContainer': {
						fontSize: '0.875rem',
					},
					'& .MuiAutocomplete-listbox': {
						color: appTokens.palette.primary,
					},
				},
			},
		},
		MuiButtonBase: {
			styleOverrides: {
				root: {
					'&:not(.MuiButton-colorError)': {
						color: appTokens.palette.primary,
					},
					'&.MuiPickerDay-root': {
						fontSize: '0.5625rem',
					},
					'&.MuiPickerDay-root:not(.MuiButton-colorError).Mui-selected, &.MuiDateRangePickerDay-root:not(.MuiButton-colorError).Mui-selected':
						{
							color: appTokens.palette.white,
						},
					'&.MuiPickerDay-root:not(.MuiButton-colorError).Mui-disabled': {
						color: appTokens.palette.readableGray,
					},
				},
			},
			defaultProps: {
				LinkComponent: BlockingLink, // also covers Button/MenuItem with href
			},
		},
		MuiDateRangePickerDay: {
			styleOverrides: {
				root: {
					'&.MuiDateRangePickerDay-insideSelection::before': {
						backgroundColor: appTokens.palette.secondary,
					},
					'&.MuiDateRangePickerDay-selectionStart::before, &.MuiDateRangePickerDay-selectionEnd::before':
						{
							backgroundColor: appTokens.palette.secondary,
						},
					'&.Mui-selected, &.Mui-selected:hover, &.Mui-selected:focus': {
						backgroundColor: appTokens.palette.primary,
						color: appTokens.palette.white,
					},
				},
			},
		},
		MuiFormControl: {
			styleOverrides: {
				root: {
					color: appTokens.palette.primary,
					'.MuiPickerSectionList-sectionContent': {
						color: appTokens.palette.primary,
					},
				},
			},
		},
		MuiListItem: {
			styleOverrides: {
				root: {
					color: appTokens.palette.primary,
				},
			},
		},
		// So that the description of our MuiModal (really a Dialog) is like the typography body1
		MuiDialogContentText: {
			defaultProps: {
				color: appTokens.palette.primary,
			},
			styleOverrides: {
				root: {
					fontSize: '1rem',
					lineHeight: '1.375rem',
				},
			},
		},
		MuiDialogActions: {
			styleOverrides: {
				root: ( { theme } ) => ( {
					[ theme.breakpoints.down( 'sm' ) ]: {
						'.MuiStack-root': {
							gap: '1rem',
						},
					},
				} ),
			},
		},
		MuiCheckbox: {
			styleOverrides: {
				root: {
					fill: appTokens.palette.primary,
				},
			},
		},
		MuiTabs: {
			styleOverrides: {
				root: {
					backgroundColor: 'transparent',
					borderRadius: 0,
					padding: 0,
					boxShadow: 'none',
					border: 'none',
					minHeight: 'auto',
					fontSize: '1rem',
				},
				indicator: {
					backgroundColor: appTokens.palette.primary,
				},
				scrollButtons: {
					color: appTokens.palette.primary,
					'&.Mui-disabled': {
						opacity: 0.3,
					},
				},
			},
		},
		MuiTab: {
			styleOverrides: {
				root: {
					padding: '0.5rem',
					borderRadius: 0,
					border: 'none',
					background: 'transparent',
					textTransform: 'none',
					transition: 'all 0.2s ease',
					cursor: 'pointer',
					position: 'relative',
					zIndex: 0,
					fontWeight: 500,
					minHeight: 0,
					minWidth: 0,
					color: appTokens.palette.readableGray,
					fontSize: '1rem',
					'&:hover': {
						backgroundColor: 'transparent',
						color: appTokens.derived.primaryLight,
					},
					'&.Mui-selected': {
						color: appTokens.palette.primary,
						fontWeight: 600,
						position: 'relative',
						'&:hover': {
							color: appTokens.derived.primaryLight,
						},
					},
				},
			},
		},
		MuiCard: {
			defaultProps: {
				elevation: defaultElevation,
			},
		},
		MuiCardActions: {
			styleOverrides: {
				root: {
					padding: '1rem 0 0 1rem',
				},
			},
		},
		MuiAlert: {
			styleOverrides: {
				root: ( { ownerState } ) => ( {
					maxWidth: '65.75rem',
					marginLeft: 'auto',
					marginRight: 'auto',
					...( ownerState.severity === 'info' && {
						backgroundColor: appTokens.palette.white,
						color: appTokens.palette.primary,
					} ),
				} ),
				// Target the icon element within the Alert
				icon: ( { ownerState } ) => ( {
					...( ownerState.severity === 'info' && {
						svg: {
							color: appTokens.palette.secondary,
						},
					} ),
				} ),
			},
		},
		MuiIconButton: {
			styleOverrides: {
				root: ( { ownerState } ) => ( {
					'&:hover': {
						svg: { fill: appTokens.derived.primaryLight },
					},
					...( ownerState.disabled === true && {
						svg: { fill: appTokens.palette.readableGray },
					} ),
				} ),
			},
		},
		MuiRadio: {
			styleOverrides: {
				root: {
					'&.Mui-disabled': {
						svg: {
							fill: appTokens.palette.readableGray,
							background:
								'radial-gradient(circle, #E5E5E5 0%, #E5E5E5 50%, #FFFFFF 51%,   #FFFFFF 100%);',
						},
					},
					'&:not(.Mui-checked):not(.Mui-disabled)': {
						svg: { fill: appTokens.derived.darkBlue },
					},
				},
			},
		},
		MuiTableCell: {
			styleOverrides: {
				root: {
					color: appTokens.palette.secondary,
					fontSize: '0.875rem',
					lineHeight: '1.375rem',
				},
			},
		},
		MuiAutocomplete: {
			styleOverrides: {
				listbox: {
					padding: 0,
					'& .MuiAutocomplete-option': {
						'&[aria-selected="true"]': {
							backgroundColor: appTokens.derived.secondaryLight,
							'&.Mui-focused': {
								backgroundColor: appTokens.derived.secondaryLight,
							},
						},
						'&.Mui-focused': {
							backgroundColor: appTokens.derived.secondaryLight,
						},
					},
					'&:has(.MuiAutocomplete-option.Mui-focused:not([aria-selected="true"]))':
						{
							'& .MuiAutocomplete-option[aria-selected="true"]': {
								backgroundColor: appTokens.derived.secondaryLightest,
							},
						},
					'& .MuiAutocomplete-groupUl': {
						padding: 0,
						display: 'block',
						'& .MuiAutocomplete-option': {
							paddingLeft: '2rem', // Indent grouped items
						},
						'& .MuiAutocomplete-groupLabel': {
							color: appTokens.palette.secondary,
							fontSize: '0.875rem',
							textTransform: 'capitalize',
							fontWeight: 600,
						},
					},
				},
			},
		},
		MuiLink: {
			defaultProps: {
				component: BlockingLink,
			},
			styleOverrides: {
				root: {
					color: appTokens.palette.primary,
					'&:hover': {
						color: appTokens.derived.primaryLight,
					},
				},
			},
		},
	},
} );

const MuiThemeProvider = ( { children }: { children?: React.ReactNode } ) => (
	<ThemeProvider theme={ theme }>{ children }</ThemeProvider>
);

export default MuiThemeProvider;
